import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useAttachments,
  useBreakpoint,
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
} from "hooks";
import React, { useCallback, useEffect, useState } from "react";
import DialogHeader from "./DialogHeader";
import {
  ArrowBackIosRounded,
  ArrowForwardIosRounded,
  UploadFile,
} from "@mui/icons-material";
import { getTranslation } from "common";
import CommentsSection, {
  Comment,
  CompleteComment,
} from "components/CommentsSection";
import { flownodesApi, organizationsApi, processesApi } from "api";
import { getLabelDataFromJsonTags, toBase64 } from "common/utilities";
import AttachmentList from "components/AttachmentList";
import { TaskCardButtonDialog } from "./TaskCardButtonsDialog";
import { Priority } from "hooks/usePriorities";
import { SequenceFlow, Task } from "model/activitiesModel";
import LabelManager from "../labels/LabelManager";
import FieldsList from "components/FieldsList";
import Labels from "components/labels/Labels";

interface BaseTaskDetailDialogProps {
  open: boolean;
  doing: boolean;
  onClose: () => void;
  nextButtonLabel?: string;
  undoButtonLabel?: string;
  handleNextStep?: (task: Task) => void;
  handleBackStep?: (task: Task) => void;
  handleSequenceFlowChoice?: (task: Task, sequenceFlow: SequenceFlow) => void;
  administrator?: boolean;
  task: Task;
  readonly?: boolean;
}

// type Sections = "userdata" | "comments" | "attachments";
const TaskDetailDialog: React.FC<BaseTaskDetailDialogProps> = ({
  open,
  doing,
  onClose,
  task,
  readonly = false,
  handleSequenceFlowChoice,
  handleNextStep,
  handleBackStep,
  nextButtonLabel,
  undoButtonLabel,
  administrator,
}) => {
  let organizationId: number | null | undefined = null;
  // const [maxWidth, setMaxWidth] = useState<number>(0);
  const [actionsWidth] = useState<number>(0);
  const isMobile = useBreakpoint("down", "sm");
  const selectedOrganization = useSelectedOrganization();
  if (selectedOrganization) {
    organizationId = selectedOrganization.id;
  }
  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  const [comments, setComments] = useState<CompleteComment[]>([]);
  //const [openUserdata, setOpenUserdata] = useState<boolean>(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [attachments, _, getAttachments, images, histories] =
    useAttachments(task);

  const [showMore, setShowMore] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [allPriorities, setAllPriorities] = useState<Priority[]>([]);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(
    null
  );

  const toggleShowMore = () => {
    setShowMore((current) => !current);
  };

  const getComments = useCallback(async () => {
    if (task) {
      try {
        const response = await flownodesApi.get(`${task.idprocess}/comments`);
        setComments([...response.data.comments]);
      } catch {
      } finally {
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onComment = async ({ comment }: Comment) => {
    if (task && comment) {
      try {
        await flownodesApi.post(`${task.idprocesstoken}/comments`, {
          comment,
        });
        getComments();
      } catch {
      } finally {
      }
    }
  };


  const handleUploadFile = async (files: FileList | null) => {
    if (task) {
      // setLoading(true);
      let promises = [];
      let hasError = false;
      try {
        if (files) {
          for (let file of Array.from(files)) {
            const payload = {
              idorganization: organizationId,
              filename: file.name,
              cloudlink: "",
              idtype: 1,
              description: file.name,
              blob: await toBase64(file),
            };
            promises.push(
              flownodesApi.post(`${task.idprocesstoken}/attachments`, payload)
            );
          }
          try {
            await Promise.all(promises);
          } catch {
            hasError = true;
          } finally {
            // setLoading(false);
          }
          if (!hasError) {
            sendSnack({
              type: "success",
              message: getTranslation(
                translations,
                "generic.fileuploadedsuccessfully"
              ),
            });
          }
          getAttachments();
        }
      } finally {
      }
    }
  };

  const fetchOrganizationPriorities = useCallback(async () => {
    // setLoading(true);
    const response = await organizationsApi.get(
      `/${organizationId}/priorities`
    );
    if (response && response.data && response.data.priorities) {
      const data: Priority[] = response.data.priorities;
      setAllPriorities(data);
      // setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (open) {
      fetchOrganizationPriorities();
    }
  }, [fetchOrganizationPriorities, open]);

  useEffect(() => {
    if (task && task.priority_name) {
      const priority = allPriorities.find(
        (element) => element.name === task.priority_name
      );
      setSelectedPriority(priority || null);
    }
  }, [allPriorities, task]);

  // const updatePriority = async (newPriority: Priority) => {
  //   if (task && task.priority_name) {
  //     const priority = allPriorities.find(
  //       (element) => element.name === task.priority_name
  //     );

  //     if (priority) {
  //       await flownodesApi.delete(
  //         `/${task?.idprocess}/priorities/${priority.id}`,
  //         {}
  //       );
  //     }
  //   }

  //   await flownodesApi.put(
  //     `/${task?.idprocess}/priorities/${newPriority.id}`,
  //     {}
  //   );

  //   await activitiesApi.get("/canget");
  // };

  // type Panel = "file" | "userdata" | "comments" | null;
  // const [openPanel, setOpenPanel] = useState<Panel>("comments");
  // const iconButtonStyle = {
  //   color: "rgba(84, 189, 193, 1)",
  //   borderRadius: "6px",
  //   padding: "2px",
  //   marginRight: "8px",
  //   backgroundColor: "rgba(84, 189, 193, 0.3)",
  //   "&:hover": {
  //     backgroundColor: "rgba(84, 189, 193, 0.4)",
  //   },
  // };

  // const CustomIconButton = styled(IconButton)(iconButtonStyle);
  // const icons = {
  //   ExpandMoreRounded: <ExpandMoreRounded />,
  // };
  const [isLabelManagerShown, setIsLabelManagerShown] = useState(false);
  const handleLabelManagerClose = () => {
    setIsLabelManagerShown(false);
  };
  const [createLabelOpen, setCreateLabelOpen] = useState(false);
  const handleOpenCreateLabel = () => {
    setCreateLabelOpen(true);
  };
  const handleCreateLabelClose = () => {
    setCreateLabelOpen(false);
  };

  // const handleOpenSection = (section: Sections) =>
  //   setOpenPanel(openPanel === "userdata" ? null : "userdata");
  const [userdataEntries, setUserDataEntries] = useState(Object.fromEntries(
    Object.entries<{ value: string; inCard: boolean }>(
      JSON.parse(task?.userdata || "{}")
    ).map(([key, value]) => [key, value.value])
  ));
  // const userdataEntries = task?.userdata && isJson(task.userdata)
  //     ? Object.entries<{ value: string, inCard: boolean }>(JSON.parse(task.userdata || "{}"))
  //     : [];

  const handleSaveUserdata = async (newUserdata: { [key: string]: string }, check: string | null) => {
    try {
      const payload =
        Object.fromEntries(Object.entries(newUserdata).map(([key, value]) => ([key, {
          value, inCard: check === key
        }])))
        ;
      const { data } = await processesApi.put(`/${task?.idprocess}/userdata`, payload);
      // setUserDataEntries(userdataEntries)
      sendSnack({
        message: data.responsemessage,
        type: "success",
      });
    }
    catch {
    }
    finally { }
  };

  useEffect(() => {
    if (open) {
      getComments();
      getAttachments();
      // getUserdata();
    }
  }, [getComments, getAttachments, open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <DialogHeader onClose={onClose}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 3 }}>
          {task && (
            <Stack>
              <Typography variant="h5">{task.flownodename} - {task.description}</Typography>
              <Typography variant="body2">{task.processmodelname}</Typography>
              {/* <Typography variant="body2"></Typography> */}
            </Stack>
          )}
          <Labels labels={getLabelDataFromJsonTags(task?.jsontag)} />
        </Stack>
      </DialogHeader>
      <DialogContent sx={{ p: 5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={10}>
            <Stack spacing={1} direction="column">
              <Stack style={{ width: "25%" }} sx={{ pt: 1 }}>
                {selectedPriority && (
                  <Tooltip
                    title={selectedPriority.name}
                    placement="right-start"
                  >
                    <Chip
                      label={selectedPriority.name}
                      color="primary"
                      sx={{
                        background: "transparent",
                        fontSize: 18,
                        fontWeight: "bold",
                        minWidth: 45,
                        px: 1,
                        py: 2,
                        borderRadius: "6px",
                        border: "1.5px solid rgba(84, 189, 193, 1)",
                        color: "black",
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
              {/*
               <Stack style={{ width: "10%" }}>
                <PrioritiesDropDown
                  allPriorities={allPriorities}
                  value={selectedPriority}
                  administrator={administrator}
                  onChange={(newPriority) => {
                    setSelectedPriority(newPriority);
                    updatePriority(newPriority);
                  }}
                />
              </Stack>
              */}
              <FieldsList
                canManage={doing && !readonly}
                selectedProcess={task?.idprocess || null}
                value={userdataEntries}
                onChange={setUserDataEntries}
                handleSaveUserdata={handleSaveUserdata}
              />
              {/* File allegati */}
              <Stack spacing={1} direction="column" alignItems="flex-start">
                <Box display="flex" width="90%">
                  <Typography variant="h6" sx={{ fontSize: "1.125rem" }}>
                    {getTranslation(translations, "settings.attachments.label")}
                  </Typography>
                </Box>
                <Stack direction="column" alignItems="flex-start">
                  <AttachmentList
                    canManage={doing && !readonly}
                    // attachments={attachments}
                    attachments={attachments.slice(
                      0,
                      showMore ? attachments.length : 4
                    )}
                    // fatherWidth={maxWidth}
                    knownWidth={actionsWidth}
                    getAttachments={getAttachments}
                    images={images}
                    histories={histories}
                  />
                  <Button onClick={toggleShowMore}>
                    {attachments.length > 4
                      ? showMore
                        ? getTranslation(
                          translations,
                          "settings.attachments.hide.label"
                        )
                        : getTranslation(
                          translations,
                          "settings.attachments.show.label"
                        )
                      : null}
                  </Button>
                </Stack>
              </Stack>

              {/* Comments */}
              <Stack spacing={1} direction="column" alignItems="flex-start">
                <Typography variant="h6" sx={{ fontSize: "1.125rem", mb: 2 }}>
                  {getTranslation(
                    translations,
                    "settings.comments.label"
                  )}
                </Typography>
                <CommentsSection
                  canWrite={doing && !readonly}
                  onComment={onComment}
                  comments={comments}
                />
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ pl: 2 }}>
            {task && (
              <Stack spacing={1}>
                {handleNextStep &&
                  (!task?.sequenceflows || task.sequenceflows?.length <= 0) && (
                    <Button
                      fullWidth
                      id={`process-next-button-${task.idprocesstoken}`}
                      style={{ display: "flex" }}
                      size="small"
                      endIcon={<ArrowForwardIosRounded />}
                      variant="semitransparent"
                      onClick={() => {
                        handleNextStep(task as Task);
                        onClose();
                      }}
                    >
                      {nextButtonLabel ||
                        getTranslation(translations, "process.action.next")}
                    </Button>
                  )}
                {task.sequenceflows &&
                  task.sequenceflows.length > 0 &&
                  handleSequenceFlowChoice && (
                    <TaskCardButtonDialog
                      task={task as Task}
                      handleSequenceFlowChoice={handleSequenceFlowChoice!}
                    />
                  )}
                {handleBackStep && (
                  <Box>
                    <Button
                      fullWidth
                      id={`process-back-button-${task?.idprocesstoken}`}
                      size="small"
                      endIcon={<ArrowBackIosRounded />}
                      variant="semitransparent"
                      onClick={() => {
                        handleBackStep(task as Task);
                        onClose();
                      }}
                    >
                      {undoButtonLabel ||
                        getTranslation(translations, "process.action.undo")}
                    </Button>
                  </Box>
                )}


                {!readonly && doing && (
                  <>
                    <Button
                      endIcon={<UploadFile />}
                      size="small"
                      variant="semitransparent"
                      component="label"
                    >
                      {getTranslation(
                        translations,
                        "pm.startprocess.button.uploadfile.caption"
                      )}
                      <input
                        id="upload-attachment-button-2"
                        type="file"
                        multiple
                        hidden
                        onChange={({ target }) =>
                          handleUploadFile(target.files)
                        }
                      />
                    </Button>
                    <Button
                      //id=""
                      //endIcon={ }
                      size="small"
                      variant="semitransparent"
                      component="label"
                      onClick={() =>
                        setIsLabelManagerShown(!isLabelManagerShown)
                      }
                    >
                      {getTranslation(translations, "generic.labels.caption")}
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      {isLabelManagerShown && (
        <LabelManager
          idprocess={task?.idprocess}
          open={isLabelManagerShown}
          handleClose={handleLabelManagerClose}
          handleOpenCreateLabel={handleOpenCreateLabel}
          createLabelOpen={createLabelOpen}
          handleCreateLabelClose={handleCreateLabelClose}
        />
      )}
    </Dialog>
  );
};
export default TaskDetailDialog;
